import {
  WalletStatus,
  WalletType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { isNil, toInteger } from "lodash";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { getWalletManagement } from "src/api/merchant";
import { ApiResult } from "src/api/types";

type InvokerAddress = {
  id: string;
  merchant_id: string;
  wallet_type: number;
  wallet_address: string;
  chain_type: number;
  chain_id: null;
  status: number;
  wallet_name: string;
  client_id: string;
  is_default: boolean;
  wallet_version: number;
  wallet_path: string;
  wallet_index: string;
  created_by: string;
  created_date: string;
  last_modified_date: string;
  data_signature: string;
  data_signature_version: number;
  data_signer_server_type: number;
  data_signer_server_id: string;
  display_name: string;
  save_records: boolean;
  risk_level: number;
  no_risk_level_count: number;
  control_groups: null;
  assign_wallet_time: string;
  smart_account_type: number;
  owner_address: null;
  supported_chains: Supportedchain[];
};

interface Supportedchain {
  chain_type: number;
  chain_id: string;
}

function useQueryInvokerAddress({
  chainType,
  chainId,
  currency,
  enabled,
  walletMajorVersion,
}: {
  chainType: string;
  chainId: string;
  currency: string;
  enabled: boolean;
  walletMajorVersion?: number;
}) {
  const invokerAddressRes = useQuery<ApiResult<InvokerAddress>>({
    queryKey: [
      "batchApproveAllowanceInvokerAddress",
      chainType,
      chainId,
      currency,
    ],
    queryFn: () => {
      if (isNil(chainType) || isNil(chainId)) {
        return Promise.resolve([]);
      }

      return getWalletManagement({
        chainType,
        chainId,
        statuses: [WalletStatus.Enabled],
        walletTypes: [WalletType.InvokerWallet],
        page: 0,
        pageSize: 100,
      });
    },
    staleTime: 60 * 60 * 12,
    enabled,
  });

  const invokerAddressSelectOptions: Record<string, string> = useMemo(() => {
    if (!invokerAddressRes.data?.rows?.length) {
      return {};
    }

    return invokerAddressRes.data.rows
      .filter((item) => {
        if (isNil(walletMajorVersion)) return true;
        return toInteger(item.wallet_version / 100) === walletMajorVersion;
      })
      .reduce(
        (acc: Record<string, string>, { wallet_address, display_name }) => ({
          ...acc,
          [`[${display_name}] ${wallet_address}`]: wallet_address,
        }),
        {},
      );
  }, [invokerAddressRes.data, walletMajorVersion]);

  const defaultAddress = useMemo(() => {
    if (!invokerAddressRes.data?.rows?.length) {
      return "";
    }

    return (
      invokerAddressRes.data.rows
        .filter((item) => {
          if (isNil(walletMajorVersion)) return true;
          return toInteger(item.wallet_version / 100) === walletMajorVersion;
        })
        .find((w) => w.is_default)?.wallet_address || ""
    );
  }, [invokerAddressRes.data, walletMajorVersion]);

  return { invokerAddressSelectOptions, defaultAddress };
}

export default useQueryInvokerAddress;
